import { motion } from "framer-motion";
import { Phone, MessageCircle, ArrowUpRight, Plane, Package } from "lucide-react";
import { CONTACT } from "@/data/content";

const heroImage =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGFpcnBsYW5lJTIwbG9hZGluZ3xlbnwwfHx8fDE3ODM4OTYwMDN8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative border-b border-[#E4E7EC] bg-[#F9FAFB]"
    >
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24 lg:py-28">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="overline mb-6 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block h-2 w-2 rounded-full bg-[#D92D20]" />
              Delhi NCR · India Desk for Global Freight Forwarders
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl font-black leading-[0.95] tracking-tight text-[#101828] sm:text-6xl md:text-7xl lg:text-[5.5rem]"
              data-testid="hero-heading"
              id="hero-heading"
            >
              Customs cleared.
              <br />
              <span className="text-[#D92D20]">India delivered.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 max-w-2xl text-lg leading-relaxed text-[#344054] md:text-xl"
              data-testid="hero-subheading"
            >
              We are a <strong>licensed Customs House Agent (CHA)</strong> in
              Delhi NCR — the trusted India customs clearance partner for
              freight forwarders in <strong>40+ countries</strong>. We clear{" "}
              <strong>air express, commercial cargo, and unaccompanied
              baggage</strong> at every major Indian airport, then deliver
              last-mile across <strong>19,000+ pincodes</strong>.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                WhatsApp our desk now
              </a>
              <a
                href={CONTACT.telHref}
                data-testid="hero-call-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-6 py-4 font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
              >
                <Phone className="h-5 w-5" strokeWidth={2.5} />
                {CONTACT.phoneDisplay}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#475467]"
            >
              <span className="font-mono-accent">24/7 OPS</span>
              <span className="h-3 w-px bg-[#E4E7EC]" />
              <span className="font-mono-accent">LICENSED CHA</span>
              <span className="h-3 w-px bg-[#E4E7EC]" />
              <span className="font-mono-accent">SERVING 40+ COUNTRIES</span>
              <span className="h-3 w-px bg-[#E4E7EC]" />
              <span className="font-mono-accent">ICEGATE eSANCHIT</span>
            </motion.div>
          </div>

          {/* Right bento */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 md:gap-4" style={{ minHeight: "520px" }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="col-span-6 row-span-3 overflow-hidden border-2 border-[#101828] bg-[#101828]"
              >
                <img
                  src={heroImage}
                  alt="Cargo aircraft loading at airport tarmac — air express customs clearance"
                  className="h-full w-full object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="col-span-3 row-span-3 border-2 border-[#101828] bg-white p-5"
              >
                <div className="flex items-center justify-between">
                  <Plane className="h-6 w-6 text-[#D92D20]" strokeWidth={2.5} />
                  <span className="font-mono-accent">01</span>
                </div>
                <div className="mt-8 font-display text-4xl font-black leading-none text-[#101828]">
                  &lt; 6 hrs
                </div>
                <p className="mt-3 text-sm leading-snug text-[#475467]">
                  Median air-express clearance turnaround at IGI Delhi.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="col-span-3 row-span-3 border-2 border-[#101828] bg-[#FDB022] p-5"
              >
                <div className="flex items-center justify-between">
                  <Package className="h-6 w-6 text-[#101828]" strokeWidth={2.5} />
                  <span className="font-mono-accent text-[#101828]">02</span>
                </div>
                <div className="mt-8 font-display text-4xl font-black leading-none text-[#101828]">
                  19,000+
                </div>
                <p className="mt-3 text-sm leading-snug text-[#101828]/80">
                  Indian pincodes covered by our last-mile network.
                </p>
              </motion.div>
            </div>

            <motion.a
              href="#services"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-[#101828] hover:text-[#D92D20]"
              data-testid="hero-scroll-link"
            >
              Explore services
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
